import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus } from 'lucide-react';


// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '0px 20px 20px 20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    verticalAlign: 'middle',
    margin: 0,
    height: '50px',
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyComments = message.startsWith('No comments found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyComments ? '16px' : '8px',
        border: isEmptyComments ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyComments ? '16.8px' : '18px',
          lineHeight: isEmptyComments ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyComments ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyComments ? '120px' : '100%',
              margin: isEmptyComments ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

// New Delete Confirmation Dialog Component
const DeleteConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  selectedComments
}) => {
  if (!isOpen) return null;

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '600px', padding: '0'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <AlertTriangle size={20} />
          <span>Confirm Deletion</span>
        </div>

        <div style={{ padding: '24px' }}>
          <p style={{
            color: '#374151',
            marginBottom: '16px',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {selectedComments.size === 1
              ? 'Are you sure you want to delete this comment?'
              : `Are you sure you want to delete these ${selectedComments.size} comments?`}
          </p>

          <p style={{
            fontSize: '12px',
            color: '#DC2626',
            marginBottom: '16px'
          }}>
            This action cannot be undone.
          </p>

          {/* Show comment details */}
          <div style={{
            backgroundColor: '#F9FAFB',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              marginBottom: '8px'
            }}>
              Comment details:
            </p>
            <div style={{
              fontSize: '14px',
              color: '#374151'
            }}>
              <div>
                <strong>Selected Comments:</strong> {selectedComments.size}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          padding: '0 24px 24px 24px'
        }}>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {selectedComments.size === 1 ? 'Delete Comment' : 'Delete Comments'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageCommentsDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  selectedLevelId,
  corporateAccount,
  currentProject,
  login_token,
  requirementIdWithPrefix,
  functionalLevelDescription,
  comingFrom = 'requirements_flow'
}) => {
  const [comments, setComments] = useState([]);
  const [selectedComments, setSelectedComments] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [commentLevel, setCommentLevel] = useState('ProjectLevel');
  const [commentForm, setCommentForm] = useState({
    comments: '',
    status: 'Active'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortCriteria, setSortCriteria] = useState('created_date desc');
  const [isInitialized, setIsInitialized] = useState(false);
  const [reqType, setReqType] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filteredComments, setFilteredComments] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [userId, setUserId] = useState('');

  // New state for update and delete functionality
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  // New state for delete confirmation dialog
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [permissions, setPermissions] = useState({
  canUpdate: false,
  canDelete: false
    });

  const commentInputRef = useRef(null);

 const checkPermission = async (categoryHeader) => {
  try {
    const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        user_id: localStorage.getItem('user_id'),
        project_id: currentProject,
        category_header: categoryHeader,
        category_sub_header: []
      }),
    });
    const data = await response.json();
    console.log('API Response:', data);

    // Create new permissions object
    const newPermissions = {
      canUpdate: false,
      canDelete: false
    };

    // Map API response to permission fields
    data.user_actions_list.forEach(item => {
      switch(item.user_action) {
        case 'Create or Edit Comments':
          newPermissions.canUpdate = item.has_access;
          break;
        case 'Delete Comments':
          newPermissions.canDelete = item.has_access;
          break;
      }
    });

    console.log('New Permissions:', newPermissions);

    // Update the state with the new permissions
    setPermissions(newPermissions);

    return data.access_status && data.status === 'Success';
  } catch (error) {
    console.error(`Error checking permission for ${categoryHeader}:`, error);
    return false;
  }
};

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setComments([]);
    setSelectedComments(new Set());
    setError('');
    setSuccessMessage('');
    setIsLoading(false);
    setSearchTerm('');
    setFilteredComments([]);
    setCurrentPage(1);
  };

  const showErrorDialog = (message) => {
    setDialogMessage(message);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to comments
  const sortedComments = React.useMemo(() => {
    let sortableComments = [...filteredComments];
    if (sortConfig.key) {
      sortableComments.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        // Handle date sorting
        if (sortConfig.key === 'created_date') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableComments;
  }, [filteredComments, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedComments?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedComments = (sortedComments || []).slice(startIndex, startIndex + itemsPerPage);

useEffect(() => {
  const initializeDialog = async () => {
    if (isOpen && !isInitialized) {
      // Reset all data
      resetAllData();
      setIsLoading(true);
      setUserId(localStorage.getItem('user_id') || '');

      // Check permissions first
      await checkPermission('Manage Comments');

      // Determine the initial comment level based on selections
      let initialLevel = 'ProjectLevel';
      let initialReqType = '';

      if (selectedRequirements.size === 1) {
        initialLevel = 'RequirementLevel';

        if (comingFrom === 'requirements_flow') {
          initialReqType = 'REQUIREMENT';
        } else {
          initialReqType = 'INTEGRATION';
        }
      } else if (selectedLevelId) {
        initialLevel = 'FunctionalLevel';
        initialReqType = '';
      } else {
        // For ProjectLevel, set req_type to empty string
        initialReqType = '';
      }

      // Set the comment level and req_type
      setCommentLevel(initialLevel);
      setReqType(initialReqType);

      await fetchComments(initialLevel, initialReqType, '');

      setIsInitialized(true);
    } else if (!isOpen) {
      // Reset initialization when dialog closes
      setIsInitialized(false);
    }
  };

  initializeDialog();
}, [isOpen]);

  // This effect handles changes in comment level
  useEffect(() => {
    console.log('Useeffect 2')

    const handleLevelChange = async () => {
      if (isOpen && isInitialized) {
        setComments([]);
        setSelectedComments(new Set());
        setError('');
        setIsLoading(true);

        await fetchComments(commentLevel, reqType);
      }
    };

    handleLevelChange();
  }, [commentLevel, reqType]);

  // Effect to handle search term changes and filtering
  useEffect(() => {
    console.log('Useeffect 3')

    if (searchTerm) {
      const filtered = comments.filter(comment =>
        comment.comments.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (comment.user_name || comment.user_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (comment.req_id_with_prefix !== 'NA' ? comment.req_id_with_prefix : comment.usecase_id_with_prefix || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (comment.level_description !== 'NA' ? comment.level_description : '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredComments(filtered);
    } else {
      setFilteredComments(comments);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchTerm, comments]);

  const fetchComments = async (level, reqTypeParam = reqType, searchQuery = searchTerm) => {
    setComments([]);

    if (!isLoading) {
      setIsLoading(true);
    }

    // Determine parameters based on level
    let levelId = null;
    let reqId = null;

    if (level === 'FunctionalLevel') {
      if (!selectedLevelId) {
        setError('Please select a functional level for Functional Level Comments');
        setIsLoading(false);
        return;
      }
      levelId = selectedLevelId;
    } else if (level === 'RequirementLevel') {
      if (selectedRequirements.size !== 1) {
        setError('Please select exactly one requirement for Requirement Level Comments');
        setIsLoading(false);
        return;
      }
      reqId = Array.from(selectedRequirements)[0];
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_comments_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + login_token
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_type: reqTypeParam,
          req_id: reqId || '',
          filter_by_status: [],
          search_query: searchQuery,
          sort_criteria: sortCriteria
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setComments(data.comments_list || []);
        setFilteredComments(data.comments_list || []);
        if (data.comments_list?.length === 0) {
          showErrorDialog('No comments found.\nCreate new comments to get started.');
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch comments');
      }
    } catch (err) {
      setComments([]);
      setFilteredComments([]);
      setError(err.message);
      console.error('Error fetching comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommentLevelChange = (newLevel) => {
    if (newLevel !== commentLevel) {
      setCommentLevel(newLevel);

      // Update the req_type based on the selected level
      if (newLevel === 'RequirementLevel') {
        if (comingFrom === 'requirements_flow') {
          setReqType('REQUIREMENT');
        } else {
          setReqType('INTEGRATION');
        }
      } else {
        // For ProjectLevel and FunctionalLevel, set req_type to empty string
        setReqType('');
      }
    }
  };

  const handleOpenCreateDialog = () => {
    setCommentForm({
      comments: ''
    });
    setError('');
    setIsCreateDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCommentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search input with Enter key support
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Search is handled by useEffect
    }
  };

  const handleSearch = () => {
    // Search is handled by useEffect
  };

  const handleCreateComment = async () => {
    setIsLoading(true);
    setError('');

    // Validate input
    if (!commentForm.comments) {
      setError('Please enter a comment');
      setIsLoading(false);
      return;
    }

    // Determine parameters based on level
    let levelId = null;
    let reqIds = [];

    if (commentLevel === 'FunctionalLevel') {
      if (!selectedLevelId) {
        setError('Please select a functional level');
        setIsLoading(false);
        return;
      }
      levelId = selectedLevelId;
    } else if (commentLevel === 'RequirementLevel') {
      if (selectedRequirements.size !== 1) {
        setError('Please select exactly one requirement');
        setIsLoading(false);
        return;
      }
      reqIds = Array.from(selectedRequirements);
    }

    try {
//      const userId = localStorage.getItem('user_id') || '';

      const response = await fetch(appConfig.getUrl('/add_comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + login_token
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_type: reqType,
          req_ids: reqIds,
          comments: commentForm.comments,
          status: 'Active',
          user_id: userId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(data.status_description || 'Comment created successfully');

        // First refresh the comments list
        await fetchComments(commentLevel);

        // Then close the dialog and clear selections
        setIsCreateDialogOpen(false);
        setCommentForm({
          comments: ''
        });

        // Clear success message after longer delay to ensure user sees it
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to create comment');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error creating comment:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // New function to handle opening the edit dialog
  const handleOpenEditDialog = (comment) => {
    setEditingComment(comment);
    setCommentForm({
      comments: comment.comments,
      status: comment.status
    });
    setError('');
    setIsEditDialogOpen(true);
  };

  // New function to handle updating a comment
  const handleUpdateComment = async () => {
    setIsLoading(true);
    setError('');

    // Validate input
    if (!commentForm.comments) {
      setError('Please enter a comment');
      setIsLoading(false);
      return;
    }

    try {
//      const userId = localStorage.getItem('user_id') || '';

      const response = await fetch(appConfig.getUrl('/update_comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + login_token
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          comment_id: editingComment.comment_id,
          comments: commentForm.comments,
          status: 'Active',
          user_id: userId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(data.status_description || 'Comment updated successfully');

        // First refresh the comments list
        await fetchComments(commentLevel);

        // Then close the dialog and clear selections
        setIsEditDialogOpen(false);
        setEditingComment(null);
        setCommentForm({
          comments: ''
        });

        // Clear success message after longer delay to ensure user sees it
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to update comment');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating comment:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Updated function to handle delete button click
  const handleDelete = async () => {
    if (selectedComments.size === 0) {
      setError('No comments selected for deletion');
      return;
    }

    if (!permissions.canDelete) {
      showErrorDialog('Insufficient permissions to delete comments');
      return;
    }

    // Show the new confirmation dialog
    setIsDeleteConfirmOpen(true);
  };

  // New function to handle confirmed deletion
  const handleDeleteConfirmed = async () => {
    setIsDeleteConfirmOpen(false);
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(appConfig.getUrl('/delete_comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + login_token
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          comment_ids: Array.from(selectedComments)
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(`Successfully deleted ${selectedComments.size} comment(s)`);
        setSelectedComments(new Set());

        // Refresh the comments list
        await fetchComments(commentLevel);

        // Clear success message after longer delay to ensure user sees it
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to delete comments');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedComments.size === paginatedComments.length) {
      setSelectedComments(new Set());
    } else {
      const newSelected = new Set(paginatedComments.map(comment => comment.comment_id));
      setSelectedComments(newSelected);
    }
  };

  const toggleRowSelection = (commentId) => {
    const newSelected = new Set(selectedComments);
    if (newSelected.has(commentId)) {
      newSelected.delete(commentId);
    } else {
      newSelected.add(commentId);
    }
    setSelectedComments(newSelected);
  };

  // Focus input when dialog opens
  useEffect(() => {
    console.log('Useeffect 4')

    if (isCreateDialogOpen && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [isCreateDialogOpen]);

  // Create Comment Dialog Content
  const renderCommentFormDialog = () => {
    if (!isCreateDialogOpen) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '900px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>Create New Comment</h2>

          {/* Level Information */}
          <div className="mb-6 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">Adding comment to:</h3>
            {commentLevel === 'ProjectLevel' ? (
              <p>Project Level (Overall project level comments)</p>
            ) : commentLevel === 'FunctionalLevel' ? (
              <p>Functional Level: <strong>{functionalLevelDescription || 'Select a functional level'}</strong></p>
            ) : (
              <p>Requirement: <strong>{requirementIdWithPrefix || 'Select a requirement'}</strong></p>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleCreateComment(); }}>
            {/* Comment Form */}
            <div style={{marginBottom: '15px', marginLeft: '10px', marginRight: '10px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>
                Comment: <span className="text-red-500">*</span>
              </label>
              <textarea
                name="comments"
                value={commentForm.comments}
                ref={commentInputRef}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '96px'
                }}
                placeholder="Enter your comment"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className={'mb-4 p-2 rounded ' + (
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              )}>
                {error}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setCommentForm({
                    comments: ''
                  });
                  setError('');
                }}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: permissions.canUpdate ? '#C8F4DE' : '#CCCCCC',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading || !permissions.canUpdate}
              >
                {isLoading ? 'Processing...' : 'Create Comment'}
              </button>
            </div>

            {!isLoading && (
              <p className="text-gray-600 text-sm mt-4 text-center">
                You can continue adding more comments. Click 'Close' when finished.
              </p>
            )}
          </form>
        </div>
      </div>
    );
  };

  // Edit Comment Dialog Content
  const renderEditDialog = () => {
    if (!isEditDialogOpen || !editingComment) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '700px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>Edit Comment</h2>

          <form onSubmit={(e) => { e.preventDefault(); handleUpdateComment(); }}>
            {/* Comment Form */}
            <div style={{marginBottom: '15px', marginLeft: '10px', marginRight: '10px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>
                Comment: <span className="text-red-500">*</span>
              </label>
              <textarea
                name="comments"
                value={commentForm.comments}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '96px'
                }}
                placeholder="Enter your comment"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className={'mb-4 p-2 rounded ' + (
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              )}>
                {error}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingComment(null);
                  setCommentForm({
                    comments: ''
                  });
                  setError('');
                }}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: permissions.canUpdate ? '#C8F4DE' : '#CCCCCC',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading || !permissions.canUpdate}
              >
                {isLoading ? 'Processing...' : 'Update Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Generate level title based on current selection
  const getLevelTitle = () => {
    if (commentLevel === 'ProjectLevel') {
      return 'Project Level Comments';
    } else if (commentLevel === 'FunctionalLevel') {
      return 'Functional Level Comments' + (functionalLevelDescription ? ' - ' + functionalLevelDescription : '');
    } else {
      return 'Requirement Level Comments' + (requirementIdWithPrefix ? ' - ' + requirementIdWithPrefix : '');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95vw',
        maxWidth: '1400px',

        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>

        {/* Header with title and search */}
   <div style={{
  backgroundColor: '#CCCCFF',
  padding: '12px 20px',
  borderRadius: '12px 12px 0 0',
  display: 'flex',
  alignItems: 'center',
  gap: '20px'
}}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <input
      type="text"
      placeholder="Search comments..."
      value={searchTerm}
      onChange={handleSearchInputChange}
      style={{
        padding: '8px 12px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px',
        width: '250px'
      }}
    />
    <button
      onClick={handleSearch}
      style={{
        padding: '8px 12px',
        backgroundColor: '#4285f4',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Search size={16} />
    </button>
  </div>

  <h1 style={{
    flex: 1,
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'black',
    margin: 0
  }}>
    Manage Comments - {getLevelTitle()}
  </h1>

  <div className="group relative">
    <button
      onClick={handleOpenCreateDialog}
      disabled={!permissions.canUpdate}
      style={{
        padding: '8px 16px',
        backgroundColor: '#4285f4',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        opacity: permissions.canUpdate ? 1 : 0.6,
      }}
    >
      <Plus size={16} />
      Create New Comment
    </button>

    {/* Hover tooltip for disabled state */}
    {!permissions.canUpdate && (
      <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
        Insufficient permission
        <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
      </div>
    )}
  </div>
</div>

        {/* Comment Level Selection */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
            Please select the level from the below options:
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="commentLevel"
                checked={commentLevel === 'ProjectLevel'}
                onChange={() => handleCommentLevelChange('ProjectLevel')}
                style={{ marginRight: '8px' }}
              />
              <span>Project Level Comments (Overall project level comments)</span>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: selectedLevelId ? 1 : 0.6
            }}>
              <input
                type="radio"
                name="commentLevel"
                checked={commentLevel === 'FunctionalLevel'}
                onChange={() => handleCommentLevelChange('FunctionalLevel')}
                disabled={!selectedLevelId}
                style={{ marginRight: '8px' }}
              />
              <span>
                Functional Level Comments: {selectedLevelId ? functionalLevelDescription : 'No functional level selected'}
              </span>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: selectedRequirements.size === 1 ? 1 : 0.6
            }}>
              <input
                type="radio"
                name="commentLevel"
                checked={commentLevel === 'RequirementLevel'}
                onChange={() => handleCommentLevelChange('RequirementLevel')}
                disabled={selectedRequirements.size !== 1}
                style={{ marginRight: '8px' }}
              />
              <span>

                Requirement Level Comments: {selectedRequirements.size === 1
                  ? requirementIdWithPrefix
                  : selectedRequirements.size > 1
                    ? 'Multiple requirements selected'
                    : 'No requirement selected'}


              </span>
            </label>
          </div>
        </div>

        {/* Error/Success Messages */}
        {(error || successMessage) && (
          <div style={{ padding: '12px 20px' }}>
            {error && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: error.includes('successfully') ? '#d4edda' : '#f8d7da',
                color: error.includes('successfully') ? '#155724' : '#721c24',
                border: `1px solid ${error.includes('successfully') ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                {error}
              </div>
            )}

            {successMessage && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#d4edda',
                color: '#155724',
                border: '1px solid #c3e6cb',
                borderRadius: '4px'
              }}>
                {successMessage}
              </div>
            )}
          </div>
        )}

        {/* Table Container */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={paginatedComments.length > 0 && selectedComments.size === paginatedComments.length}
                    onChange={toggleSelectAll}
                    disabled={paginatedComments.length === 0}
                    style={{ marginRight: '8px' }}
                  />
                  Select All
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    color: '#4285f4',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => handleSort('created_date')}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Created Date
                    {sortConfig.key === 'created_date' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    color: '#4285f4',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => handleSort('user_name')}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    User
                    {sortConfig.key === 'user_name' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    color: '#4285f4',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => handleSort('req_id_with_prefix')}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Requirement
                    {sortConfig.key === 'req_id_with_prefix' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    color: '#4285f4',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => handleSort('level_description')}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Functional Level
                    {sortConfig.key === 'level_description' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    Loading...
                  </td>
                </tr>
              ) : paginatedComments.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    No comments found. Create a new comment or adjust your search.
                  </td>
                </tr>
              ) : (
                paginatedComments.map((comment, index) => (
                  <React.Fragment key={comment.comment_id}>
                    {/* First row with main data */}
                    <tr
                      style={{
                        backgroundColor: selectedComments.has(comment.comment_id) ? '#e3f2fd' : (index % 2 === 0 ? '#fafafa' : 'white'),
                        borderBottom: 'none'
                      }}
                    >
                      <td style={{ padding: '8px 8px 4px 8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedComments.has(comment.comment_id)}
                          onChange={() => toggleRowSelection(comment.comment_id)}
                        />
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {new Date(comment.created_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {comment.user_name || comment.user_id}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {comment.req_id_with_prefix !== 'NA' ? comment.req_id_with_prefix :
                         comment.usecase_id_with_prefix ? comment.usecase_id_with_prefix : '-'}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {comment.level_description !== 'NA' ? comment.level_description : '-'}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px' }}>
                        {permissions.canUpdate && (
                          <button
                            onClick={() => handleOpenEditDialog(comment)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#4285f4',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Second row with comment */}
                    <tr
                      style={{
                        backgroundColor: selectedComments.has(comment.comment_id) ? '#e3f2fd' : (index % 2 === 0 ? '#fafafa' : 'white'),
                        borderBottom: '1px solid #dee2e6'
                      }}
                    >
                      <td style={{ padding: '4px 8px 8px 8px' }}></td>
                      <td colSpan="5" style={{
                        padding: '4px 8px 8px 8px',
                        fontSize: '12px',
                        fontStyle: 'italic',
                        color: '#555'
                      }}>
                        <div style={{ wordWrap: 'break-word', lineHeight: '1.4', color: 'purple' }}>
                          <strong>Comment:</strong> {comment.comments}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && sortedComments.length > 0 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            fontSize: '14px'
          }}>
            <div style={{ color: '#666' }}>
              Total Entries: {sortedComments.length} | Showing {sortedComments.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedComments.length)} of {sortedComments.length}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  color: '#4285f4'
                }}
              >
                ‹
              </button>
              <span style={{ color: '#4285f4' }}>
                Page {currentPage} of {Math.max(1, totalPages)}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{
                  padding: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1,
                  color: '#4285f4'
                }}
              >
                ›
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}

        {/* Footer */}
  <div style={{
  padding: '12px 20px',
  borderTop: '1px solid #dee2e6',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#f8f9fa'
}}>
  <div className="group relative">
    <button
      onClick={handleDelete}
      disabled={isLoading || selectedComments.size === 0 || !permissions.canDelete}
      style={{
        padding: '8px 16px',
        backgroundColor: selectedComments.size > 0 && !isLoading && permissions.canDelete ? '#dc3545' : '#FF8F89',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px'
      }}
    >
      {isLoading ? 'Deleting...' : `Remove Selected (${selectedComments.size})`}
    </button>

    {/* Delete button tooltip */}
    {(selectedComments.size === 0 || !permissions.canDelete) && (
      <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
        {selectedComments.size === 0
          ? "Select one or more comments to delete"
          : "Insufficient permission"
        }
        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
      </div>
    )}
  </div>

  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
    {selectedComments.size > 0 && (
      <span style={{ fontSize: '14px', color: '#666' }}>
        {selectedComments.size} selected
      </span>
    )}
    <button
      onClick={onClose}
      style={{
        padding: '8px 16px',
        backgroundColor: '#FFFDD0',
        color: '#333',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px'
      }}
    >
      Close
    </button>
  </div>
</div>

      </div>

      {/* Dialogs */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
        }}
        onConfirm={handleDeleteConfirmed}
        selectedComments={selectedComments}
      />

      {/* Create Comment Dialog */}
      {renderCommentFormDialog()}

      {/* Edit Comment Dialog */}
      {renderEditDialog()}
    </div>
  );
};

export default ManageCommentsDialog;